"use client";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useImperativeHandle, useId, useRef, useState, } from "react";
import { useDetectInsideInteraction, useMeasure, useViewport, } from "@travel/shared/hooks";
import { IconButton } from "../../buttons";
import { Overlay } from "../../overlays";
import { DropDownFooter } from "../drop-down-footer/drop-down-footer";
import CloseIcon from "@travel/shared/assets/icons/icon-close.svg";
import classNames from "classnames";
import styles from "./drop-down.module.css";
export var DEFAULT_DROP_DOWN_STATE = {
    isFocused: false,
    isOpen: false,
};
var INITIAL_POSITION = {};
export var DropDownV2 = forwardRef(function DropDown(_a, ref) {
    var _b;
    var _c;
    var className = _a.className, contentClassName = _a.contentClassName, _d = _a.open, open = _d === void 0 ? false : _d, _e = _a.anchor, anchor = _e === void 0 ? "left" : _e, trigger = _a.trigger, content = _a.content, footer = _a.footer, contentMinWidth = _a.contentMinWidth, _f = _a.contentDesiredHeight, contentDesiredHeight = _f === void 0 ? 200 : _f, _g = _a.contentOffset, contentOffset = _g === void 0 ? "0px" : _g, _h = _a.fillMobile, fillMobile = _h === void 0 ? true : _h, _j = _a.unmountOnExit, unmountOnExit = _j === void 0 ? false : _j, _k = _a.scrollLock, scrollLock = _k === void 0 ? true : _k, onOpen = _a.onOpen, onClose = _a.onClose, onOpenChange = _a.onOpenChange, onClickClose = _a.onClickClose;
    var id = useId();
    var _l = __read(useState(open), 2), isOpen = _l[0], setIsOpen = _l[1];
    var _m = __read(useState(false), 2), isFocused = _m[0], setIsFocused = _m[1];
    var _o = __read(useState(INITIAL_POSITION), 2), _p = _o[0], setPosition = _o[1], contentPosition = _p.content, position = __rest(_p, ["content"]);
    var isSmall = useViewport().isSmall;
    var containerRef = useRef();
    var contentRef = useRef();
    var triggerRef = useRef();
    var triggerCloneRef = useRef();
    var panelContentRef = useRef();
    var ignoreNextBlurFromInsideClickRef = useRef(false);
    var shouldAutoFocusRef = useRef(false);
    var state = __assign(__assign({}, DEFAULT_DROP_DOWN_STATE), { isFocused: isFocused, isOpen: isOpen });
    var _q = __read(useMeasure(["bounds"]), 2), measureRef = _q[0], bounds = _q[1].bounds;
    var _r = __read(useMeasure([
        "bounds",
    ]), 2), measureContentRef = _r[0], contentBounds = _r[1].bounds;
    var interactionRef = useDetectInsideInteraction(function (isInside, event, isNode) {
        if (isNode) {
            blur();
        }
    }, isOpen, undefined);
    useImperativeHandle(ref, function () {
        return {
            focus: function () {
                var _a, _b, _c, _d, _e, _f;
                (_c = (_b = (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.el) === null || _b === void 0 ? void 0 : _b.focus) === null || _c === void 0 ? void 0 : _c.call(_b);
                (_f = (_e = (_d = triggerRef.current) === null || _d === void 0 ? void 0 : _d.el) === null || _e === void 0 ? void 0 : _e.click) === null || _f === void 0 ? void 0 : _f.call(_e);
            },
            blur: blur,
        };
    }, []);
    useEffect(function () {
        var _a;
        if (((_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.el) === document.activeElement) {
            shouldAutoFocusRef.current = true;
        }
    }, []);
    useEffect(function () {
        checkPosition();
    }, [
        bounds,
        containerRef.current,
        panelContentRef.current,
        contentRef.current,
    ]);
    useEffect(function () {
        if (!isOpen)
            return;
        checkPosition();
        window.addEventListener("resize", checkPosition);
        return function () {
            window.removeEventListener("resize", checkPosition);
        };
    }, [isOpen]);
    useEffect(function () {
        setIsOpen(open);
    }, [open]);
    useEffect(function () {
        var _a, _b, _c, _d;
        if (!triggerRef.current)
            return;
        var isFocusing = false;
        var onFocus = function (event) {
            if (isFocusing)
                return;
            isFocusing = true;
            setTimeout(function () {
                checkPosition();
                setIsOpen(true);
                focus();
            }, 10);
            setTimeout(function () {
                isFocusing = false;
                checkPosition();
            }, 100);
        };
        var onBlur = function (event) {
            if (isFocusing)
                return;
            if (ignoreNextBlurFromInsideClickRef.current) {
                ignoreNextBlurFromInsideClickRef.current = false;
                event.stopPropagation();
                return;
            }
            blur();
        };
        (_a = triggerRef.current.el) === null || _a === void 0 ? void 0 : _a.addEventListener("click", onFocus);
        (_b = triggerRef.current.el) === null || _b === void 0 ? void 0 : _b.addEventListener("focus", onFocus);
        (_d = (_c = triggerCloneRef.current) === null || _c === void 0 ? void 0 : _c.el) === null || _d === void 0 ? void 0 : _d.addEventListener("blur", onBlur);
        if (shouldAutoFocusRef.current) {
            console.log("EVENT::SHOULD_AUTO_FOCUS");
            shouldAutoFocusRef.current = false;
            focus();
            setIsOpen(true);
        }
        return function () {
            var _a, _b, _c, _d, _e, _f;
            (_b = (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.el) === null || _b === void 0 ? void 0 : _b.removeEventListener("click", onFocus);
            (_d = (_c = triggerRef.current) === null || _c === void 0 ? void 0 : _c.el) === null || _d === void 0 ? void 0 : _d.removeEventListener("focus", onFocus);
            (_f = (_e = triggerCloneRef.current) === null || _e === void 0 ? void 0 : _e.el) === null || _f === void 0 ? void 0 : _f.removeEventListener("blur", onBlur);
        };
    }, [(_c = triggerRef.current) === null || _c === void 0 ? void 0 : _c.el]);
    /*useEffect(() => {
      if (panelContentRef.current) {
        checkPosition();
      }
    }, [panelContentRef.current]);
    */
    return (_jsx("div", { className: classNames(styles["container"], styles["anchor-".concat(anchor)], className), ref: function (el) {
            measureRef(el);
            containerRef.current = el;
        }, children: _jsx(Overlay, { open: isOpen, trigger: trigger(state, triggerRef), lockScrollOnOpened: true, onClickTrigger: checkPosition, className: classNames(styles["overlay"], (_b = {},
                _b[styles["fill-mobile"]] = fillMobile,
                _b)), classNames: {
                enter: styles["enter"],
                enterActive: styles["enter-active"],
                exit: styles["exit"],
                exitActive: styles["exit-active"],
            }, content: _jsxs("div", { className: styles["panel"], style: __assign({ position: "absolute" }, position), ref: function (el) {
                    interactionRef(el);
                    contentRef.current = el;
                }, onMouseDown: handleOnContentClick, children: [_jsxs("div", { className: styles["top"], style: { maxWidth: position === null || position === void 0 ? void 0 : position.minWidth }, children: [trigger(state, triggerCloneRef, true), _jsx(IconButton, { className: styles["back"], icon: _jsx(CloseIcon, { width: 32, height: 32 }), onClick: function (event) {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    blur();
                                    onClickClose === null || onClickClose === void 0 ? void 0 : onClickClose();
                                } })] }), _jsxs("div", { className: classNames(styles["content"], contentClassName), style: __assign({ minWidth: contentMinWidth, maxWidth: contentMinWidth }, contentPosition), ref: function (el) {
                            measureContentRef(el);
                            panelContentRef.current = el;
                        }, children: [content(state), footer && (_jsx(DropDownFooter, { className: styles["footer"], children: footer }))] })] }), unmountOnExit: unmountOnExit, scrollLock: isSmall && fillMobile ? true : scrollLock, trap: false, hideClose: true, onOpenChange: function (isOpen) {
                if (!isOpen) {
                    setIsFocused(false);
                }
                if (isOpen) {
                    checkPosition();
                }
                setIsOpen(isOpen);
                onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(isOpen);
            }, onOpen: onOpen, onClose: onClose }) }));
    function focus() {
        var _a, _b;
        checkPosition();
        try {
            (_b = (_a = triggerCloneRef.current) === null || _a === void 0 ? void 0 : _a.focus) === null || _b === void 0 ? void 0 : _b.call(window);
        }
        catch (error) {
            console.log(error);
        }
        setIsFocused(true);
    }
    function blur() {
        setIsFocused(false);
        setIsOpen(false);
    }
    function handleOnContentClick() {
        ignoreNextBlurFromInsideClickRef.current = true;
    }
    function checkPosition() {
        var _a, _b, _c;
        var rect = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        if (rect) {
            var contentRect = (_b = panelContentRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
            if (contentRect) {
                var top_1 = scrollLock
                    ? rect === null || rect === void 0 ? void 0 : rect.top
                    : ((_c = containerRef.current) === null || _c === void 0 ? void 0 : _c.offsetTop) || 0;
                var contentLeft = anchor === "right"
                    ? -((contentRect === null || contentRect === void 0 ? void 0 : contentRect.width) || 0) + ((rect === null || rect === void 0 ? void 0 : rect.width) || 0)
                    : 0;
                var viewportHeight = window.innerHeight;
                var initialContentTop = (rect === null || rect === void 0 ? void 0 : rect.bottom) || 0;
                var marginTop = contentOffset;
                var contentTop = 0;
                var contentBottom = "";
                var maxHeight = "calc(100vh - ".concat(initialContentTop, "px)");
                var borderOffset = 0;
                var contentPosition_1 = "relative";
                if (initialContentTop + contentDesiredHeight > viewportHeight) {
                    contentTop = -(contentRect === null || contentRect === void 0 ? void 0 : contentRect.height) - rect.height;
                    marginTop = "-".concat(marginTop);
                    maxHeight = "calc(100vh - (100vh - ".concat(rect.top, "px))");
                    borderOffset = -borderOffset;
                    contentBottom = "100%";
                    contentPosition_1 = "absolute";
                }
                setPosition({
                    minWidth: "".concat(rect.width, "px"),
                    top: "".concat(top_1, "px"),
                    left: "".concat(rect.left, "px"),
                    content: {
                        marginTop: "calc(".concat(contentOffset, " - ").concat(borderOffset, "px)"),
                        top: "auto",
                        bottom: contentBottom,
                        left: "".concat(contentLeft, "px"),
                        maxHeight: maxHeight,
                        position: contentPosition_1,
                    },
                });
            }
            return true;
        }
        return false;
    }
});
